import { useCallback, useEffect, useState } from 'react'
import { History, Undo2 } from 'lucide-react'
import { opcoApi } from '../../../../services/api'
import { useToast } from '../../../../context/ToastContext'
import { ConfirmDialog, Modal, PanelState, asList, cardCls, errorMessage, secondaryBtn } from '../shared'
import { canUndoRun, runStatusLabel } from './helpers'

function when(iso) {
  if (!iso) return ''
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

// Every bulk apply, newest first. Undo removes exactly what a run added; anything an admin changed
// afterwards is left alone and shown as "changed since".
export default function RunsPanel({ reloadKey }) {
  const toast = useToast()
  const [runs, setRuns] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [detail, setDetail] = useState(null)
  const [undoing, setUndoing] = useState(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await opcoApi.listAssignRuns()
      setRuns(asList(res?.data, 'runs'))
    } catch (err) {
      setError(errorMessage(err, 'Failed to load the runs'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load, reloadKey])

  const open = async (run) => {
    try {
      const res = await opcoApi.getAssignRun(run.uid)
      setDetail(res?.data?.run || null)
    } catch (err) {
      toast.error('Could not open the run', errorMessage(err))
    }
  }

  const confirmUndo = async () => {
    setBusy(true)
    try {
      const res = await opcoApi.undoAssignRun(undoing.uid)
      const run = res?.data?.run || {}
      toast.success('Run undone', `${run.undone ?? 0} removed. Anyone changed since was left alone.`)
      setUndoing(null)
      await load()
    } catch (err) {
      const code = err?.response?.data?.code
      toast.error(code === 'not_undoable' ? 'Already undone' : 'Could not undo', errorMessage(err))
      setUndoing(null)
      await load()
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className={cardCls} aria-label="Runs">
      <div className="border-b border-gray-100 dark:border-gray-800 px-5 py-4">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Runs</h2>
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Each time you place existing people it is recorded here, so you can see exactly who was added and undo it.</p>
      </div>
      <PanelState loading={loading} error={error} onRetry={load} empty={runs.length === 0} emptyText="Nothing has been placed yet.">
        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {runs.map((r) => (
            <li key={r.uid} className="flex flex-wrap items-center gap-3 px-5 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-900 dark:text-gray-100">
                  {when(r.created_at)} <span className="text-xs text-gray-500">by {r.created_by_email || 'unknown'}</span>
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {runStatusLabel(r.status)} - {r.added} added
                  {r.skipped ? `, ${r.skipped} skipped` : ''}{r.failed ? `, ${r.failed} failed` : ''}
                  {r.undone ? `, ${r.undone} undone` : ''}
                </p>
              </div>
              <button type="button" onClick={() => open(r)} className={secondaryBtn}>Details</button>
              {canUndoRun(r) && (
                <button type="button" onClick={() => setUndoing(r)} className={secondaryBtn}>
                  <Undo2 className="h-4 w-4" /> Undo
                </button>
              )}
            </li>
          ))}
        </ul>
      </PanelState>

      {detail && (
        <Modal title={`Run of ${when(detail.created_at)}`} onClose={() => setDetail(null)} wide labelledBy="opco-run-title">
          <p className="mb-3 text-xs text-gray-500">{runStatusLabel(detail.status)} - {detail.added} added, {detail.skipped} skipped, {detail.failed} failed, {detail.undone} undone</p>
          <div className="max-h-96 overflow-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase text-gray-500">
                <tr><th className="py-1 pr-3">Person</th><th className="py-1 pr-3">OpCo</th><th className="py-1 pr-3">Result</th><th className="py-1">Undo</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {(detail.items || []).map((i) => (
                  <tr key={i.user_id}>
                    <td className="py-1.5 pr-3">{i.email || i.user_id}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs">{i.opco_code}</td>
                    <td className="py-1.5 pr-3">{i.result === 'skipped_changed' ? 'skipped (changed)' : i.result}{i.error ? `: ${i.error}` : ''}</td>
                    <td className="py-1.5 text-xs text-gray-500">{i.undo_result === 'skipped_changed' ? 'changed since, kept' : (i.undo_result || '')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}
      {undoing && (
        <ConfirmDialog
          title="Undo this run?"
          message={`The ${undoing.added} people it added lose that OpCo membership. Anyone whose membership was changed since is left alone.`}
          confirmLabel="Undo"
          danger
          busy={busy}
          onConfirm={confirmUndo}
          onCancel={() => setUndoing(null)}
        />
      )}
    </section>
  )
}
